import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const CARD = { silahli: "Silahlı", silahsiz: "Silahsız" };
export const DAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

export function useData(url, params) {
  const [data, setData] = useState(null);
  const key = JSON.stringify(params || {});
  const load = useCallback(async () => {
    if (!url) return;
    try {
      setData((await api.get(url, { params: JSON.parse(key) })).data);
    } catch (e) {
      setData([]);
      toast.error(errMsg(e));
    }
  }, [url, key]);
  useEffect(() => {
    load();
  }, [load]);
  return [data, load];
}

export function Loading() {
  return <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--red)]" /></div>;
}

export function Title({ title, desc, children }) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-extrabold text-zinc-950" data-testid="ops-page-title">{title}</h1>
        {desc && <p className="mt-1 text-sm text-zinc-500">{desc}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export function Pill({ ok, children }) {
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${ok ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{children}</span>;
}

function Input({ f, value, set }) {
  const id = `f-${f.k}`;
  const tid = `form-${f.k}`;
  const val = value ?? "";
  const onChange = (e) => set(e.target.value);
  if (f.type === "select") {
    return <select id={id} className="field-input" data-testid={tid} required={f.required} value={val} onChange={onChange}>{f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
  }
  if (f.type === "textarea") return <textarea id={id} className="field-input" data-testid={tid} required={f.required} rows={3} value={val} onChange={onChange} />;
  if (f.type === "bool") {
    return <input id={id} type="checkbox" checked={!!value} onChange={(e) => set(e.target.checked)} className="h-5 w-5 accent-[color:var(--red)]" data-testid={tid} />;
  }
  return <input id={id} className="field-input" data-testid={tid} required={f.required} type={f.type || "text"} step={f.step} value={val} onChange={onChange} />;
}

const toPayload = (fields, v) => Object.fromEntries(fields.map((f) => {
  const x = v[f.k];
  if (f.type === "number") return [f.k, x === "" || x === undefined || x === null ? null : Number(x)];
  if (f.empty === null && !x) return [f.k, null];
  return [f.k, x ?? (f.type === "bool" ? false : "")];
}));

function FieldRow({ f, value, set }) {
  const isBool = f.type === "bool";
  const span = f.wide || f.type === "textarea" ? "sm:col-span-2" : "";
  const layout = isBool ? "flex items-center gap-2 text-sm" : "grid gap-1";
  return (
    <label htmlFor={`f-${f.k}`} className={`text-xs font-semibold text-zinc-600 ${span} ${layout}`}>
      {isBool && <Input f={f} value={value} set={set} />}
      <span>{f.label}{f.required && <span className="text-[color:var(--red)]"> *</span>}</span>
      {!isBool && <Input f={f} value={value} set={set} />}
      {f.hint && <span className="font-normal text-zinc-400">{f.hint}</span>}
    </label>
  );
}

export function FormDialog({ open, onClose, title, fields, initial, onSubmit }) {
  const [v, setV] = useState(initial || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setV(initial || {});
  }, [initial, open]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSubmit(toPayload(fields, v));
      onClose(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="ops-form-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
          {fields.map((f) => <FieldRow key={f.k} f={f} value={v[f.k]} set={(x) => setV((prev) => ({ ...prev, [f.k]: x }))} />)}
          <div className="flex justify-end gap-2 sm:col-span-2">
            <button type="button" onClick={() => onClose(false)} className="btn-outline !py-2">Vazgeç</button>
            <button disabled={busy} className="btn-red !py-2" data-testid="form-submit">{busy ? "Kaydediliyor..." : "Kaydet"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export async function confirmRun(msg, fn, ok) {
  if (!window.confirm(msg)) return;
  try {
    await fn();
    toast.success(ok);
  } catch (e) {
    toast.error(errMsg(e));
  }
}

export function Table({ head, children, testid }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500"><tr>{head.map((h) => <th key={h} className="whitespace-nowrap px-3 py-3">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-zinc-100">{children}</tbody>
      </table>
    </div>
  );
}

export const fmtDay = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("tr-TR") : "-");
export const fmtDT = (d) => (d ? new Date(d).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul", dateStyle: "short", timeStyle: "short" }) : "-");
export const todayTR = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Istanbul" });
